import { Resources, type TFunction } from "Translations/Resources";
import {
  PHONE_FALLBACK_MAX_DIGITS,
  PHONE_FALLBACK_MIN_DIGITS,
} from "Utils/PhoneUtils";
import { removeNonDigits } from "Utils/StringUtils";
import { z } from "zod";

const TRANSLATION_RESOURCES = Resources.Validation.Phone;

// Digits with optional spaces — spaces are stripped by the backend before persisting.
const NATIONAL_NUMBER_REGEX = /^[\d ]*$/;

const countDigits = (value: string) => (removeNonDigits(value) ?? "").length;

type PhoneSchemaOptions = {
  /** With `isRequired: false` an empty number is valid (prefix keeps its default). */
  isRequired?: boolean;
};

/**
 * Phone as { prefix, number } matching the API `PhoneNumber` shape.
 * The prefix comes from the code-list dropdown; the number is the national part only.
 */
export const getPhoneSchema = (
  t: TFunction,
  { isRequired = true }: PhoneSchemaOptions = {},
) =>
  z.object({
    prefix: z.string().min(1, t(TRANSLATION_RESOURCES.InvalidFormat)),
    number: z
      .string()
      .trim()
      .regex(NATIONAL_NUMBER_REGEX, t(TRANSLATION_RESOURCES.InvalidFormat))
      .refine(
        (val) =>
          (!isRequired && countDigits(val) === 0) ||
          countDigits(val) >= PHONE_FALLBACK_MIN_DIGITS,
        {
          message: t(TRANSLATION_RESOURCES.MinDigits, {
            count: PHONE_FALLBACK_MIN_DIGITS,
          }),
        },
      )
      .refine((val) => countDigits(val) <= PHONE_FALLBACK_MAX_DIGITS, {
        message: t(TRANSLATION_RESOURCES.MaxCharacters, {
          count: PHONE_FALLBACK_MAX_DIGITS,
        }),
      }),
  });
